import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
    Shield, Upload, FileText, Check, X, Clock, 
    ArrowLeft, Trash2, AlertCircle, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerificationPage = () => {
    const { token, user } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [documentType, setDocumentType] = useState('');
    const [verificationStatus, setVerificationStatus] = useState('pending');
    const fileInputRef = useRef(null);

    const documentTypes = [
        { value: 'national_id', label: 'National ID Card' },
        { value: 'birth_certificate', label: 'Birth Certificate' },
        { value: 'school_letter', label: 'School Letter / ID' },
        { value: 'passport', label: 'Passport' },
        { value: 'sports_license', label: 'Sports License' },
        { value: 'guardian_consent', label: 'Guardian Consent Form' }
    ];

    useEffect(() => {
        fetchDocuments();
        fetchAthleteStatus();
    }, []);

    const fetchDocuments = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/verification/my-documents`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDocuments(response.data);
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAthleteStatus = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/athletes/me/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setVerificationStatus(response.data.verification_status || 'pending');
        } catch (error) {
            console.error('Failed to fetch status:', error);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file || !documentType) {
            toast.error('Please select a document type first');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            await axios.post(
                `${API_URL}/api/verification/upload?document_type=${documentType}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            toast.success('Document uploaded successfully');
            setDocumentType('');
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload document');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDeleteDocument = async (documentId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;

        try {
            await axios.delete(`${API_URL}/api/verification/documents/${documentId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Document deleted');
            fetchDocuments();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete document');
        }
    };

    const handleSubmitVerification = async () => {
        try {
            await axios.post(`${API_URL}/api/verification/submit`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Verification request submitted!');
            setVerificationStatus('under_review');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit verification');
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved':
            case 'verified':
                return <CheckCircle className="w-5 h-5 text-green-400" />;
            case 'rejected':
                return <X className="w-5 h-5 text-red-400" />;
            case 'under_review':
                return <Clock className="w-5 h-5 text-yellow-400" />;
            default:
                return <Clock className="w-5 h-5 text-muted-foreground" />;
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            approved: 'bg-green-500/20 text-green-400',
            verified: 'bg-green-500/20 text-green-400',
            rejected: 'bg-red-500/20 text-red-400',
            under_review: 'bg-yellow-500/20 text-yellow-400',
            pending: 'bg-gray-500/20 text-gray-400'
        };
        return styles[status] || styles.pending;
    };

    return (
        <div className="min-h-screen bg-[#0a1628]">
            {/* Header */}
            <div className="bg-[#0d2847] border-b border-white/5">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <div className="flex items-center gap-4">
                        <Link to="/profile" className="text-muted-foreground hover:text-white">
                            <ArrowLeft className="w-5 h-5" />
                        </Link>
                        <div>
                            <h1 className="font-heading font-bold text-2xl uppercase tracking-tight text-white flex items-center gap-2">
                                <Shield className="w-6 h-6 text-primary" />
                                Profile Verification
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                Upload documents to verify your athlete profile
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
                {/* Verification Status */}
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {getStatusIcon(verificationStatus)}
                            <div>
                                <h2 className="font-bold text-white">Verification Status</h2>
                                <span className={`text-sm px-2 py-1 ${getStatusBadge(verificationStatus)}`}>
                                    {verificationStatus.replace('_', ' ').toUpperCase()}
                                </span>
                            </div>
                        </div>
                        {verificationStatus === 'pending' && documents.some(d => d.status === 'pending') && (
                            <Button onClick={handleSubmitVerification} className="bg-primary" data-testid="submit-verification-btn">
                                Submit for Review
                            </Button>
                        )}
                    </div>

                    {verificationStatus === 'verified' && (
                        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-green-400">Your profile is verified! You now have a verified athlete badge.</span>
                        </div>
                    )}

                    {verificationStatus === 'rejected' && (
                        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <span className="text-red-400 block">Your verification was rejected.</span>
                                <span className="text-sm text-muted-foreground">
                                    Please upload new documents and resubmit for review.
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Upload Section */}
                {verificationStatus !== 'verified' && (
                    <div className="bg-[#0d2847] border border-white/5 p-6">
                        <h2 className="font-heading font-bold text-lg uppercase mb-4 text-white flex items-center gap-2">
                            <Upload className="w-5 h-5 text-primary" />
                            Upload Documents
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <Label>Document Type</Label>
                                <Select value={documentType} onValueChange={setDocumentType}>
                                    <SelectTrigger className="mt-1 bg-[#0a1628] border-white/10" data-testid="document-type-select">
                                        <SelectValue placeholder="Select document type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {documentTypes.map(type => (
                                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Upload File</Label>
                                <div className="mt-1">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.pdf,.webp"
                                        onChange={handleFileUpload}
                                        disabled={!documentType || uploading}
                                        className="hidden"
                                        data-testid="document-file-input"
                                    />
                                    <Button
                                        onClick={() => fileInputRef.current?.click()}
                                        variant="outline"
                                        className="w-full border-white/10"
                                        disabled={!documentType || uploading}
                                        data-testid="upload-document-btn"
                                    >
                                        {uploading ? 'Uploading...' : 'Select File'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <p className="text-xs text-muted-foreground">
                            Accepted formats: JPG, PNG, PDF, WebP. Max size: 10MB
                        </p>
                    </div>
                )}

                {/* Documents List */}
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h2 className="font-heading font-bold text-lg uppercase mb-4 text-white flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        Your Documents ({documents.length})
                    </h2>

                    {loading ? (
                        <div className="text-center py-8 text-muted-foreground">Loading...</div>
                    ) : documents.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No documents uploaded yet
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {documents.map((doc) => (
                                <div 
                                    key={doc.id} 
                                    className="flex items-center justify-between p-4 bg-[#0a1628] border border-white/5"
                                    data-testid={`document-${doc.id}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <FileText className="w-8 h-8 text-primary" />
                                        <div>
                                            <div className="font-medium text-white">
                                                {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {doc.file_name} • {new Date(doc.uploaded_at).toLocaleDateString()}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-xs px-2 py-1 ${getStatusBadge(doc.status)}`}>
                                            {doc.status}
                                        </span>
                                        {doc.status === 'pending' && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleDeleteDocument(doc.id)}
                                                className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                                data-testid={`delete-document-${doc.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Guidelines */}
                <div className="bg-[#0d2847] border border-white/5 p-6">
                    <h2 className="font-heading font-bold text-lg uppercase mb-4 text-white">
                        Verification Guidelines
                    </h2>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                        <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            Upload clear, readable copies of your documents
                        </li>
                        <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            At least one government-issued ID (National ID, Passport, or Birth Certificate)
                        </li>
                        <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            School letter or ID if you're a student athlete
                        </li>
                        <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            Guardian consent form required for athletes under 18
                        </li>
                        <li className="flex items-start gap-2">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            Verification typically takes 1-3 business days
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default VerificationPage;
